/**
 * ============================================================================
 * Accept Invitation Page
 * ============================================================================
 *
 * Landing page for the link sent by Supabase Auth's inviteUserByEmail
 * (see server/routers-supabase.ts: createUserWithInvitation).
 *
 * Supabase's client SDK (detectSessionInUrl: true, see lib/supabase.ts)
 * parses the access/refresh tokens from the URL hash on load and
 * establishes a session automatically. This page just waits for that
 * session, then lets the user set a password to finish activating
 * their account.
 *
 * ============================================================================
 */

import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { supabase } from '../lib/supabase';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, AlertCircle } from 'lucide-react';
import { APP_LOGO, APP_TITLE } from '../const';

export default function AcceptInvitation() {
  const [, setLocation] = useLocation();
  const [checkingSession, setCheckingSession] = useState(true);
  const [sessionReady, setSessionReady] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // detectSessionInUrl already parsed the invite link's tokens by the
    // time this effect runs; just confirm a session was established.
    supabase.auth.getSession().then(({ data }) => {
      setSessionReady(!!data.session);
      setCheckingSession(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setSessionReady(!!session);
    });

    return () => listener.subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;
      setLocation('/dashboard');
    } catch (err: any) {
      console.error('[AcceptInvitation] Failed to set password:', err);
      setError(err.message || 'Failed to set password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-gradient-to-br from-background to-muted">
      <div className="mb-8 flex flex-col items-center gap-4">
        <img
          src={APP_LOGO}
          alt={APP_TITLE}
          className="h-16 w-16 rounded-lg border-border bg-background object-cover"
        />
        <div className="text-center">
          <h1 className="text-3xl font-bold">{APP_TITLE}</h1>
        </div>
      </div>

      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Welcome</CardTitle>
          <CardDescription>
            {checkingSession
              ? 'Verifying your invitation...'
              : sessionReady
                ? 'Set a password to activate your account'
                : 'This invitation link is invalid or has expired'}
          </CardDescription>
        </CardHeader>

        <CardContent>
          {checkingSession ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : sessionReady ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                className="w-full bg-gradient-mypa hover:opacity-90"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Activating...
                  </>
                ) : (
                  'Activate account'
                )}
              </Button>
            </form>
          ) : (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Please ask an administrator to send you a new invitation.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
